import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { entityMemberApi, EntityMemberScroll } from "_shared/entity-members/_api/entity-member-api";
import type EntityMemberQuery from "_shared/entity-members/_api/queries/entity-member-query";
import {
  getEntityMemberPagesByQuery,
  getErrorLoadingEntityMemberPagesByQuery,
  getIsLoadingEntityMemberPagesByQuery,
  scrolledEntityMemberPageReceived,
} from "_shared/entity-members/_stores/entity-members-redux-slice";
import type Guid from "_shared/guids/guid";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

const PAGE_SIZE = 25;

type UseInfiniteEntityMembersOptions = {
  skip?: boolean;
};

/**
 * Loads a legal entity's members one page at a time, for a list that loads more when it scrolls to
 * its end. A new search starts again from the first page. Copies `useInfiniteUserCoworkers`.
 *
 * Its requests carry a `scroll`, so RTK Query caches them apart from every other reader of the
 * roster, and each result is handed to the store whether it was fetched or answered from the cache.
 */
export default function useInfiniteEntityMembers(
  legalEntityId: Guid,
  query: EntityMemberQuery,
  options?: UseInfiniteEntityMembersOptions,
) {
  const skip = options?.skip ?? false;
  const dispatch = useAttorneyHubDispatch();
  const members = useSelector(getEntityMemberPagesByQuery(legalEntityId, query));
  const isLoading = useSelector(getIsLoadingEntityMemberPagesByQuery(legalEntityId, query));
  const error = useSelector(getErrorLoadingEntityMemberPagesByQuery(legalEntityId, query));
  const requestsRef = useRef(new Set<{ abort?: () => void; unsubscribe?: () => void }>());
  const queryRef = useRef(query);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const dispatchPage = useCallback(
    (pageIndex: number, scroll: EntityMemberScroll) => {
      const pageQuery = queryRef.current.withPagination(
        PaginationParameters.create({ pageIndex, pageSize: PAGE_SIZE }),
      );
      const request = dispatch(
        entityMemberApi.endpoints.getEntityMemberPage.initiate({
          legalEntityId: legalEntityId.value,
          query: pageQuery,
          scroll,
        }),
      );
      requestsRef.current.add(request);
      void request.then((result) => {
        if (result.data) {
          dispatch(
            scrolledEntityMemberPageReceived({
              legalEntityId: legalEntityId.value,
              query: pageQuery,
              scroll,
              page: result.data,
            }),
          );
        }
      });
      void request.finally(() => {
        requestsRef.current.delete(request);
        request.unsubscribe?.();
      });
      return request;
    },
    [dispatch, legalEntityId.value],
  );

  useEffect(() => {
    if (skip) return;
    const request = dispatchPage(0, "start");
    return () => {
      request.unsubscribe?.();
    };
  }, [dispatchPage, query.cacheKey, skip]);

  useEffect(
    () => () => {
      for (const request of requestsRef.current) {
        request.unsubscribe?.();
      }
      requestsRef.current.clear();
    },
    [],
  );

  const loadMore = useCallback(() => {
    if (skip || isLoading || !members.hasNextPage) return;
    dispatchPage(members.pageIndex + 1, "next");
  }, [dispatchPage, isLoading, members.hasNextPage, members.pageIndex, skip]);

  const retry = useCallback(() => {
    if (skip || isLoading) return;
    const hasLoadedPage = members.pageSize > 0;
    dispatchPage(hasLoadedPage ? members.pageIndex + 1 : 0, hasLoadedPage ? "next" : "start");
  }, [dispatchPage, isLoading, members.pageIndex, members.pageSize, skip]);

  return useMemo(
    () => ({
      members,
      isLoading,
      isInitialLoading: isLoading && members.isEmpty,
      error,
      loadMore,
      retry,
    }),
    [error, isLoading, loadMore, members, retry],
  );
}
